import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Container, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Noti from "../../../util/noti";
import { useUser } from "../../../util/user";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import { useUserProps } from "../../../util/userDBContext";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import styles from "./SiteCreation.module.css";
import Loader from "./../../general/Loader";
import { useHistory } from "../../../util/router";
import { getDepartmentId } from "util/department";
import { isOMUser } from "../../../util/roles";
import { isSiteConstructionComplete } from "../../../util/siteCreationValidation";

export const ConstructionForm = (props) => {
	const { loggedCountry } = useUserProps();
	const user = useUser();

	const history = useHistory();
	const constructionTablePath =
		"/solar/administration/site-creation/manage-site/construction-table";

	const [clientList, setClientList] = useState([]);
	const [contractList, setContractList] = useState([]);
	const [inverterListLegacy, setInverterListLegacy] = useState([]); // this is to store inverter value as `${brand} ${model_name}` not the "model" which is sent from the device and logger itself
	const inverterList = useMemo(() => inverterListLegacy.map((inv) => ({ ...inv, label: inv.label, value: inv.model })), [inverterListLegacy]); // this is to store inverter value as "model" which is sent from the device and logger itself, this is the value that we will save in inverters.model field
	const consInvOnceLoaded = useRef(false);
	const [panelList, setPanelList] = useState([]);
	const [bessModelList, setBessModelList] = useState([]);
	const [bessMeterTypes, setBessMeterTypes] = useState([]);
	const [originalInverters, setOriginalInverters] = useState([]); // this is to store original inverters data fetched from the backend for comparison when saving, to check if inverters data has been modified or not
	const [editableInverters, setEditableInverters] = useState([]);
	const [isLoadingInverters, setIsLoadingInverters] = useState(false);

	const [listRetrieved, setListRetrieved] = useState(false);

	const [siteData, setSiteData] = useState({});
	const { register, handleSubmit, errors, setValue, getValues, watch, control, reset } =
		useForm();
	const [isReadOnly, setIsReadOnly] = useState(true);

	const siteView = user.roles.includes("administration:SiteCreation:_view");
	const siteCons = user.roles.includes("administration:SiteCreation:_editCons");
	const siteAdmin = user.roles.includes("administration:SiteCreation:_admin");
	const approveCons = user.roles.includes("administration:SiteCreation:_approvalCons");
	const canAutoSend = siteAdmin || approveCons;
	const consApproval =
		approveCons &&
		isSiteConstructionComplete(siteData) &&
		siteData?.isRejected === 2 &&
		siteData?.isSent === 1 &&
		(siteAdmin || siteData?.sender_dept_id === getDepartmentId(user.roles));

	const isExu = !!siteData?.exu_site;

	const invoiceMeterList = [
		{ label: "EDMI", value: 1, group: "default" },
		{
			label: "None-EDMI (Schneider, etc.)",
			value: 0,
			group: "default",	
		}
	];

	const soloMultiList = [
		{ label: "Multi", value: 0 },
		{ label: "Solo", value: 1 },
	];

	const countries = [
		{
			label: "Vietnam",
			value: "VN",
			group: "default",
			disabled: loggedCountry !== "VN" && siteCons,
		},
		{
			label: "Thailand",
			value: "TH",
			group: "default",
			disabled: loggedCountry !== "TH" && siteCons,
		},
		{
			label: "Cambodia",
			value: "KH",
			group: "default",
			disabled: loggedCountry !== "KH" && siteCons,
		},
		{
			label: "Philippines",
			value: "PH",
			group: "default",
			disabled: loggedCountry !== "PH" && loggedCountry !== "KH" && siteCons,
		},
	];

	const [needLoader, setNeedLoader] = useState(false); // need loader or not
	const [showLoader, setShowLoader] = useState({ position: "relative" });

	const inverterEditableFields = [
		"inv_no",
		"serial_number",
		"brand",
		"model",
		"model_name",
		"power",
		"kwp",
		"installation_date",
		"comment",
	];

	const notRequiredFields = useCallback((type) => {
		if (type === "is_no_PV") {
			return {
				// { $field: $defaultValue }
				// default to have some values to not break the rest of the code that still expects PV values for compatibility
				power: 0,
				nb_irr_sensors: 1,
				nb_inj_points: 1,
				nb_inverter: 1,
				inverter_id: null,
				EDMI_meter: 1,
				nb_solar_modules: 1,
				panel: null,
				nb_mod_temp: 1,
			};
		}
		if (type === "is_FIT_contract") {
			return {
				reference_voltage: null,
			}
		}
		return {};
	}, []);

	const loaderMounted = (boo) => {
		if (boo) {
			setShowLoader({ position: "fixed" });
			setNeedLoader(true);
		} else {
			setShowLoader({ position: "relative" });
			setNeedLoader(false);
		}
	};

	const formatDate = (_date) => {
		// we use == instead of !== because we want to compare null with undefined
		if (_date == undefined) return null;
		const date_ = new Date(_date);
		return moment(date_).format("YYYY-MM-DD");
	};

	const formatBoolean = (bool) => (bool ? 1 : 0);

	const redundancy = (arr) => {
		const reducedArr = arr.map((el) => {
			// Loop the property names of `el`, creating a new object
			// with the ones whose values aren't `null` or `undefined` or empty string.
			// `reduce` is commonly used for doing this:
			return Object.keys(el).reduce((newObj, key) => {
				const value = el[key];
				if (value !== null && value !== undefined && value !== "")
					newObj[key] = value;
				return newObj;
			}, {});
		});

		return reducedArr;
	};

	const getLabel = (_obj) => {
		if (!_obj) return null;
		else return _obj.label;
	};

	const getID = (_obj) => {
		if (!_obj) return null;
		else {
			if (_obj.__isNew__) return null;
			else if (isNaN(parseFloat(_obj.value))) return null;
			else return _obj.value;
		}
	};

	const compareObj = (a, b) => {
		// we use != instead of !== because we want to compare string number with number
		const obj_ = Object.entries(b)
			.filter(([key, val]) => a[key] != val && key in a)
			.reduce((a, [key, v]) => ({ ...a, [key]: v }), {});
		return obj_;
	};

	const compareInverters = (original, current) => {
		const changes = [];
		const inv_nos = new Set([...original.map((inv) => inv.inv_no), ...current.map((inv) => inv.inv_no)]);
		inv_nos.forEach((inv_no) => {
			const origInv = original.find((inv) => inv.inv_no === inv_no);
			const currInv = current.find((inv) => inv.inv_no === inv_no);
			if(origInv && currInv) {
				const change = compareObj(origInv, currInv);
				if (Object.keys(change).length > 0) {
					changes.push({ inv_no, ...change });
				}
			} else if(origInv && !currInv) {
				changes.push({ inv_no, ...origInv, isDeleted: true });
			} else if(!origInv && currInv) {
				changes.push({ inv_no, ...currInv, isNew: true });
			}
		});

		return changes;
	};

	const trimStr = (str) => {
		if (!str) return str;
		return str.replace(/^\s+/g, "");
	};

	const formatCoordinate = (text) => {
		if (typeof text === "string") {
			const cor = text.split(","); // an array
			if (cor.length === 2) {
				// return true
				cor[0] = cor[0].trim(); // remove white space
				cor[1] = cor[1].trim();

				if (!isNaN(cor[0]) && !isNaN(cor[1])) {
					if (cor[0] !== "" && cor[1] !== "") {
						if (
							cor[0] >= -90 &&
							cor[0] <= 90 &&
							cor[1] >= -180 &&
							cor[1] <= 180
						)
							return `${cor[0]}, ${cor[1]}`;
					}
				}
			}

			errors.general = "Wrong format of GPS coordinate";
		}

		return null;
	};

	const client = watch("client");
	const panel = watch("panel");
	const site_name = watch("site_name", siteData?.site_name || ""); // either undefined or empty
	const SF_id = watch("SF_id", siteData?.SF_id || "");
	const contract_duration = watch(
		"contract_duration",
		siteData?.contract_duration || "",
	);
	const city = watch("city", siteData?.city || "");
	const address = watch("address", siteData?.address || "");
	const zipcode = watch("zipcode", siteData?.zipcode || "");
	const GPS = watch("GPS", siteData?.GPS || "");
	const power = watch("power", siteData?.power || "");
	const msa_price = watch("msa_price", siteData?.msa_price || "");
	const nb_irr_sensors = watch(
		"nb_irr_sensors",
		siteData?.nb_irr_sensors || "",
	);
	const nb_inj_points = watch("nb_inj_points", siteData?.nb_inj_points || "");
	const nb_inverter = watch("nb_inverter", siteData?.nb_inverter || "");
	const nb_solar_modules = watch(
		"nb_solar_modules",
		siteData?.nb_solar_modules || "",
	);
	const nb_amb_temp = watch("nb_amb_temp", siteData?.nb_amb_temp || "");
	const nb_mod_temp = watch("nb_mod_temp", siteData?.nb_mod_temp || "");
	const nb_grid_meter = watch("nb_grid_meter", siteData?.nb_grid_meter || "");
	const DGPT2 = watch("DGPT2", siteData?.DGPT2 || "");
	const water = watch("water", siteData?.water || "");
	const wind_dir = watch("wind_dir", siteData?.wind_dir || "");
	const wind_speed = watch("wind_speed", siteData?.wind_speed || "");
	const comment = watch("comment", siteData?.comment || "");
	const cod = watch(
		"cod",
		siteData?.cod ? moment(siteData.cod).format("YYYY-MM-DD") : "",
	);
	const sync = watch("sync", siteData?.sync ? moment(siteData.sync) : "");
	const country = watch("country", siteData?.country || loggedCountry);
	const invoicingmeter = watch(
		"EDMI_meter",
		invoiceMeterList.find((el) => el.value == siteData?.EDMI_meter)?.value ||
			invoiceMeterList[0].value,
	);
	const is_BESS = watch("is_BESS", siteData?.is_BESS || false);
	const BESS_model = watch(
		"BESS_model",
		is_BESS ? siteData?.BESS_model || null : null,
	);
	const BESS_nb_cabinets = watch(
		"BESS_nb_cabinets",
		is_BESS ? siteData?.BESS_nb_cabinets || 1 : 0,
	);
	const BESS_capacity = watch(
		"BESS_capacity",
		is_BESS ? siteData?.BESS_capacity || 0 : null,
	);
	const BESS_meter_type = watch(
		"BESS_meter_type",
		is_BESS ? siteData?.BESS_meter_type || null : null,
	);
	const is_no_PV = watch(
		"is_no_PV",
		is_BESS ? siteData?.is_no_PV || false : false,
	);
	const reference_voltage = watch(
		"reference_voltage",
		siteData?.reference_voltage || "",
	);
	const table_name = watch("table_name", siteData?.table_name || "");
	const contract_id = watch("contract_id", siteData?.contract_id || null);

	const coeff_kpmax = watch("coeff_kpmax", siteData?.coeff_kpmax || "");

	const onEditableInverterFieldChange = (index, field, value) => {
		setEditableInverters((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
	};

	// Computes and syncs nb_inverter + dominant inverter_id from a given rows array.
	const syncDerivedInverterFields = (rows) => {
		setValue("nb_inverter", rows.length);

		// Find the model with the highest total kwp across all rows
		const kwpByModel = {};
		rows.forEach((r) => {
			if (!r.model || !r.brand) return;
			const key = `${r.brand}||${r.model}`;
			kwpByModel[key] = (kwpByModel[key] || 0) + (parseFloat(r.kwp) || 0);
		});
		const dominantKey = Object.keys(kwpByModel).sort(
			(a, b) => kwpByModel[b] - kwpByModel[a],
		)[0];
		if (dominantKey) {
			const [dBrand, dModel] = dominantKey.split("||");
			const matchedOption = inverterListLegacy.find(
				(opt) => opt.brand === dBrand && opt.model === dModel,
			);
			if (matchedOption) setValue("inverter_id", matchedOption.value);
		} else {
			setValue("inverter_id", null);
		}
	};

	const addEditableInverterRow = () => {
		setEditableInverters((prev) => {
			const next = [
				...prev,
				{
					inv_no: "INV-" + String(prev.length + 1).padStart(2, "0"),
					serial_number: "",
					brand: "",
					model: "",
					model_name: "",
					power: "",
					table_name: table_name || siteData?.table_name || "",
					kwp: 0,
					installation_date: null,
					comment: "",
				},
			];
			syncDerivedInverterFields(next);
			return next;
		});
	};

	const removeEditableInverterRow = (index) => {
		setEditableInverters((prev) => {
			const next = prev
				.filter((_, i) => i !== index)
				.map((row, i) => ({ ...row, inv_no: "INV-" + String(i + 1).padStart(2, "0") }));
			syncDerivedInverterFields(next);
			return next;
		});
	};

	const duplicateEditableInverterRow = (index) => {
		setEditableInverters((prev) => {
			const source = { ...prev[index], serial_number: "" };
			const next = [
				...prev.slice(0, index + 1),
				source,
				...prev.slice(index + 1),
			].map((row, i) => ({ ...row, inv_no: "INV-" + String(i + 1).padStart(2, "0") }));
			syncDerivedInverterFields(next);
			return next;
		});
	};

	const buildInverterPayload = (resolvedTableName) => {
		if (!resolvedTableName) return [];

		const mappedInvs = editableInverters
			.map((row) => {
				const rowPayload = {
					table_name: resolvedTableName,
				};

				inverterEditableFields.forEach((field) => {
					const rawValue = row?.[field];
					if (field === "installation_date") {
						rowPayload[field] = rawValue
							? moment(rawValue).format("YYYY-MM-DD")
							: null;
						return;
					}

					if (field === "kwp") {
						if (!rawValue) {
							rowPayload[field] = null;
						} else {
							const parsed = parseFloat(rawValue);
							rowPayload[field] = isNaN(parsed) ? null : parsed;
						}
						return;
					}

					rowPayload[field] = !rawValue ? null : rawValue;
				});

				return rowPayload;
			});
			const filteredInvs = mappedInvs.filter((row) => {
				return (
					row.inv_no ||
					row.serial_number ||
					row.model ||
					row.model_name ||
					row.brand
				);
			});

		return filteredInvs;
	};

	const validateInverterRows = () => {
		const requiredFields = ["serial_number", "brand", "model", "model_name", "power", "installation_date"];
		for (let i = 0; i < editableInverters.length; i++) {
			const row = editableInverters[i];
			for (const field of requiredFields) {
				const val = row[field];
				if (val === "" || val === null || val === undefined) {
					return `Inverter row ${i + 1}: "${field}" is required.`;
				}
			}
		}
		return null;
	};

	const getList = async () => {
		const uriClient = siteAdmin
			? `/select/clients/tempandreal/all`
			: `/select/clients/tempandreal/${loggedCountry}`;
		const uriContract = siteAdmin
			? `/select/contracts`
			: `/select/contracts/${loggedCountry}`;
		const uriInverter = `/select/inverters`;
		const uriPanel = `/select/panels/tempandreal`;
		const uriBessModels = `/select/BESS/models`;
		const uriBessMeterTypes = `/select/BESS/meter-types`;

		try {
			const [client, contract, inverter, panel, bessModels, bessMeterTypes] =
				await Promise.all([
					GYAxios.get(uriClient),
					GYAxios.get(uriContract),
					GYAxios.get(uriInverter),
					GYAxios.get(uriPanel),
					GYAxios.get(uriBessModels),
					GYAxios.get(uriBessMeterTypes),
				]).catch((error) => {
					console.log(error);
					const msg = "Error fetching lists. Please contact your IT support.";
					Noti.create({ type: "e", msg });
					return;
				});

			if (client.data) {
				const tempClient = client.data.map((v) => {
					if (v.value === null) return { label: v.label, value: v.label };
					return { label: v.label, value: v.value };
				});

				setClientList(tempClient);
			}
			if (contract.data) {
				const tempContract = contract.data.map((v) => {
					return { label: v.label, value: v.value };
				});
				setContractList(tempContract);
			}
			if (inverter.data) {
				const tempInverter = inverter.data.map((v) => {
					return {
						label: v.label,
						value: v.value,
						brand: v.brand,
						model: v.model,
						model_name: v.model_name,
						power: v.power,
					};
				});
				setInverterListLegacy(tempInverter);
			}
			if (panel.data) {
				const tempPanel = panel.data.map((v) => {
					if (v.value === null) return { label: v.label, value: v.label };
					return { label: v.label, value: v.value };
				});
				setPanelList(tempPanel);
			}
			if (bessModels.data) {
				const tempBessModels = bessModels.data.map((v) => {
					return { label: v.label, value: v.value };
				});
				setBessModelList(tempBessModels);
			}
			if (bessMeterTypes.data) {
				const tempBessMeterTypes = bessMeterTypes.data.map((v) => {
					return { label: v.label, value: v.value };
				});
				setBessMeterTypes(tempBessMeterTypes);
			}

			setListRetrieved(true);
		} catch (error) {
			console.log(error);
			const msg = "Error fetching lists. Please contact your IT support.";
			Noti.create({ type: "e", msg });
		}
	};

	const fieldShouldDisabled = ({ type, field, check }) => check && field in notRequiredFields(type);
	const setDefaultValForNotRequiredField = ({ type }) => {
		Object.entries(notRequiredFields(type)).forEach(([key, value]) => {
			setValue(key, value);
		});
	};

	function renderSelect(
		type,
		onChange,
		list,
		value,
		defVal = null,
		disabled = false,
	) {
		const selectStyle = {
			container: () => ({ float: "none" }),
			control: (provided) => ({ ...provided, height: "40px" }),
		};

		switch (type) {
			case "cr":
				return (
					<CreatableSelect
						isSearchable={true}
						isDisabled={isReadOnly || disabled}
						isClearable={!isReadOnly}
						options={list}
						value={list.find((c) => c.value === value)}
						onChange={(val) => onChange(val)}
						isLoading={list.length === 0}
						styles={selectStyle}
					/>
				);
			case "se":
				return (
					<Select
						isSearchable={true}
						isDisabled={isReadOnly || disabled}
						options={list}
						value={list.find((c) => c.value === value)}
						onChange={(val) => onChange(val.value)}
						isLoading={list.length === 0}
						styles={selectStyle}
						isOptionDisabled={(option) => option.disabled}
						defaultValue={defVal}
					/>
				);
			default:
				return null;
		}
	}

	function renderDate(val, onChange, disabled = false) {
		const isDisabled = isReadOnly || disabled;
		return (
			<DatePicker
				selected={val ? new Date(val) : null}
				onChange={(date) => onChange(date)}
				dateFormat={"yyyy-MM-dd"}
				className={`${styles.inputField} ${isDisabled ? styles.disabled : styles.buttonDateEdit}`}
				disabled={isDisabled}
				onKeyDown={(e) => {
					e.preventDefault();
				}}
			/>
		);
	}

	function renderInput(
		valName,
		len,
		require = false,
		type = "text",
		disabled = false,
	) {
		let val = eval(valName) || getValues()[valName];
		const maxNum = type === "number" ? getMaxNum(len) : "";

		return (
			<input
				type={type}
				className={styles.inputField}
				value={val !== null ? val : ""}
				{...register(valName, { required: require })}
				onChange={(e) => setValue(valName, trimStr(e.target.value))}
				maxLength={len}
				max={maxNum}
				disabled={isReadOnly || disabled}
			/>
		);
	}

	const getMaxNum = (len) => Math.pow(10, len) - 1;

	async function createNewConsSite(newInfo, inverters = null) {
		const uri = `/site_creation/cons_info/new`;

		// Auto-send if user has admin/approvalCons role and site info is complete
		const shouldAutoSend = canAutoSend && isSiteConstructionComplete(newInfo);

		const param = {
			...newInfo,
			requestedBy: user.email,
			dateCreated: formatDate(moment()),
			lastUpdatedBy: user.email,
			lastUpdatedAt: formatDate(moment()),
			...(Array.isArray(inverters) ? { inverters } : {}),
			...(shouldAutoSend ? { isSent: 1, isRejected: 2, sender_dept_id: getDepartmentId(user.roles), sentBy: user.email } : {}),
		};

		try {
			await GYAxios.post(uri, param).then(() => {
				Noti.create({ type: "s", msg: "Saved!" });
				const timer = setTimeout(() => {
					history.push(constructionTablePath);
				}, 500);

				return () => clearTimeout(timer);
			});
		} catch (error) {
			console.log(error);
			const msg =
				"Error in creating a new info for construction. Please contact your IT support.";
			Noti.create({ type: "e", msg });
		}
	}

	async function updateConsSite(amend_info, inverters = null) {
		if (siteData.isSent === 1 && siteData.isRejected !== 1) {
			const ques =
				"This site has been sent to Department Manager for approval. Are you sure you want to amend it?";
			if (!window.confirm(ques)) return;
		}

		const uri = `/site_creation/cons_info/update/${siteData.site_id}`;

		// Check if the merged (current + changes) site data is complete
		const mergedSiteData = { ...siteData, ...amend_info };
		const shouldAutoSend = canAutoSend && isSiteConstructionComplete(mergedSiteData);

		const param_01 = {
			...amend_info,
			...(shouldAutoSend ? { isSent: 1, isRejected: 2, sender_dept_id: getDepartmentId(user.roles), sentBy: user.email } : { isSent: 0, isRejected: 2 }),
			lastUpdatedBy: user.email,
			lastUpdatedAt: formatDate(moment()),
			...(Array.isArray(inverters) ? { inverters } : {}),
		};

		try {
			await GYAxios.patch(uri, param_01).then(() => {
				Noti.create({ type: "s", msg: "Saved!" });
			});
		} catch (error) {
			console.log(error);
			const msg = "Error in updating info. Please contact your IT support.";
			Noti.create({ type: "e", msg });
		}

		if (siteData.isSent === 1 && siteData.exu_site === null) {
			loaderMounted(true);
			try {
				const param = {
					...param_01,
					country: siteData.country,
					site_name: siteData.site_name,
					temp_client_name: siteData.temp_client_name,
					role: "administration:SiteCreation:_approvalCons",
					email: user.email,
					department_id: getDepartmentId(user.roles),
				};
				const uri = "/site_creation/sendEmailToDepManagerForNoticeAmendment";
				await GYAxios.post(uri, param)
					.then((res) => {
						Noti.create({ type: "s", msg: res.data.message });
						const timer = setTimeout(() => {
							history.push(constructionTablePath);
						}, 500);

						return () => clearTimeout(timer);
					})
					.catch((error) => {
						console.log(error);
						const msg =
							"Error in sending email. Please contact your IT support.";
						Noti.create({ type: "e", msg });
						loaderMounted(false);
						return;
					});

				loaderMounted(false);
			} catch (err) {
				Noti.create({
					type: "e",
					msg: "Cannot send email. Please check user's email and name and try again",
				});
				loaderMounted(false);
			}
		} else {
			const timer = setTimeout(() => {
				history.push(constructionTablePath);
			}, 500);

			return () => clearTimeout(timer);
		}
	}

	async function approveSite() {
		const question = `Are you sure you want to approve this site?`;
		const confirm = window.confirm(question);
		if (!confirm) return;

		loaderMounted(true);

		const param = {
			...siteData,
			isRejected: 0,
			decidedEmail: user.email,
		};

		try {
			await GYAxios.post("/site_creation/approveConsSite", param).then(
				(res) => {
					Noti.create({ type: "s", msg: `Approved.\n${res.data.message}` });
				},
			);
		} catch (err) {
			const msg = "Error in approving site. Please contact your IT support.";
			Noti.create({ type: "e", msg });
			loaderMounted(false);
			return;
		}
		try {
			await GYAxios.post("/site_creation/send/pm", param);
			const timer = setTimeout(() => {
				loaderMounted(false);
				history.push(constructionTablePath);
			}, 500);

			return () => clearTimeout(timer);
		} catch (err) {
			const msg = "Error in approving site. Please contact your IT support.";
			Noti.create({ type: "e", msg });
			loaderMounted(false);
		}
	}

	async function rejectSite() {
		const newRemark = prompt("Tell us the reason:", "");
		if (newRemark == null || newRemark?.trim() == "") return;

		loaderMounted(true);

		const param = {
			...siteData,
			isRejected: 1,
			decidedEmail: user.email,
			remark: newRemark,
		};

		try {
			await GYAxios.post("/site_creation/rejectSite", param).then((res) => {
				Noti.create({ type: "s", msg: `Rejected.\n${res.data.message}` });
				const timer = setTimeout(() => {
					loaderMounted(false);
					history.push(constructionTablePath);
				}, 500);

				return () => clearTimeout(timer);
			});
		} catch (err) {
			const msg = "Error in rejecting site. Please contact your IT support.";
			loaderMounted(false);
			Noti.create({ type: "e", msg });
		}
	}

	function onSubmit(rawData) {

		// remove table_name as it doesn't exist in construction_info table
		// tbale_name is used only for fetching inverters
		let data = { ...rawData };
		delete data.table_name;
		const originalData = { ...siteData };
		delete originalData.table_name;

		const table_name = siteData.table_name;

		if (Object.keys(data).length === 0) {
			const msg = "There is nothing to save.";
			Noti.create({ type: "w", msg });
			return;
		}

		if (originalData.site_id) {
			// update data
			// finding cod and sync of current site data
			originalData.cod = formatDate(originalData.cod);
			originalData.sync = formatDate(originalData.sync);

			data.temp_client_name = getLabel(data.client);
			data.client_id = getID(data.client);
			data.temp_solar_module = getLabel(data.panel);
			data.solar_module_id = getID(data.panel);

			// format BESS boolean fields
			data.is_BESS = formatBoolean(data.is_BESS);
			data.is_no_PV = formatBoolean(data.is_no_PV);

			const filteredChanges = compareObj(originalData, data);
			if (Object.keys(filteredChanges).length === 0) {
				const msg = "There is nothing to save.";
				Noti.create({ type: "w", msg });
				return;
			}

			const formatChanges = Object.entries(filteredChanges).map(
				([key, val]) => {
					if (key === "cod" || key === "sync") {
						return { [key]: formatDate(val) };
					}
					if (key === "GPS") {
						return { [key]: formatCoordinate(filteredChanges.GPS) };
					}
					return { [key]: val };
				},
			);

			if (filteredChanges.GPS != "" && formatChanges[0]?.GPS === null) {
				Noti.create({ type: "e", msg: "GPS is invalid." });
				return;
			}

			const combineChanges = Object.assign({}, ...formatChanges);
			const realChanges = compareObj(originalData, combineChanges);

			const realInvChanges = compareInverters(originalInverters, editableInverters);

			if (Object.keys(realChanges).length === 0 && realInvChanges.length === 0) {
				const msg = "There is nothing to save.";
				Noti.create({ type: "w", msg });
				return;
			}

			const values = Object.values(realChanges);
			for (let i = 0; i < values.length; i++) {
				const parsed = parseFloat(values[i]);
				if (!isNaN(parsed)) {
					if (parsed < 0) {
						const msg = "Please enter a positive number.";
						Noti.create({ type: "e", msg });
						return;
					}
				}
			}

			const inverterPayload = is_no_PV
				? []
				: buildInverterPayload(table_name);

			if (!is_no_PV) {
				const inverterError = validateInverterRows();
				if (inverterError) {
					Noti.create({ type: "e", msg: inverterError });
					return;
				}
			}

			updateConsSite(realChanges, inverterPayload);
		} else {
			// create new data
			const filteredData = redundancy([data])[0];

			filteredData.cod = formatDate(filteredData.cod);
			filteredData.sync = formatDate(filteredData.sync);
			filteredData.GPS = formatCoordinate(filteredData.GPS);
			filteredData.temp_client_name = getLabel(filteredData.client);
			filteredData.client_id = getID(filteredData.client);
			filteredData.temp_solar_module = getLabel(filteredData.panel);
			filteredData.solar_module_id = getID(filteredData.panel);
			// format BESS boolean fields
			filteredData.is_BESS = formatBoolean(filteredData.is_BESS);
			filteredData.is_no_PV = formatBoolean(filteredData.is_no_PV);
			delete filteredData["client"];
			delete filteredData["panel"];
			const newData = redundancy([filteredData])[0];

			if (Object.keys(errors).length !== 0) {
				const msg = errors.general
					? errors.general
					: "There is something wrong in your filled info.";
				Noti.create({ type: "e", msg });
				return;
			}

			const values = Object.values(newData);
			for (let i = 0; i < values.length; i++) {
				const parsed = parseFloat(values[i]);
				if (!isNaN(parsed)) {
					if (parsed < 0) {
						const msg = "Please enter a positive number.";
						Noti.create({ type: "e", msg });
						return;
					}
				}
			}

			const inverterPayload = is_no_PV
				? []
				: buildInverterPayload(table_name);

			if (!is_no_PV) {
				const inverterError = validateInverterRows();
				if (inverterError) {
					Noti.create({ type: "e", msg: inverterError });
					return;
				}
			}

			createNewConsSite(newData, inverterPayload);
		}
	}

	useEffect(() => {
		const timer = setTimeout(async () => {
			await getList();
		}, 500);

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (props.siteInfo) {
			setSiteData(props.siteInfo);
		}
	}, [props.siteInfo]);

	useEffect(() => {
		register("client");
		register("panel");
		register("solo");
		register("cod");
		register("sync");
		register("power");
		register("msa_price");
		register("nb_irr_sensors");
		register("nb_inj_points");
		register("nb_inverter");
		register("nb_solar_modules");
		register("nb_grid_meter");
		register("nb_amb_temp");
		register("nb_mod_temp");
		register("DGPT2");
		register("water");
		register("wind_dir");
		register("wind_speed");
		register("is_BESS");
		register("BESS_capacity");
		register("BESS_model");
		register("BESS_nb_cabinets");
		register("is_no_PV");
		register("reference_voltage");
		register("coeff_kpmax");
		register("comment");
	}, [register]);

	useEffect(() => {
		if (siteData && listRetrieved) {
			// == instead of !== because we want to compare null with undefined
			let defaultValues = {};
			if (siteData.site_id == null && siteCons) setIsReadOnly(false);

			Object.entries(siteData).forEach(([key, val]) => {
				if (key === "temp_client_name" && val !== null) {
					defaultValues["client"] = clientList.find((c) => {
						return c.label === val;
					});
					setValue("client", defaultValues["client"]);
				}

				if (key === "temp_solar_module" && val !== null) {
					defaultValues["panel"] = panelList.find((c) => {
						return c.label === val;
					});
					setValue("panel", defaultValues["panel"]);
				}
				defaultValues[key] = siteData[key];
			});

			if (
				defaultValues?.["requestedBy"] === user.email ||
				defaultValues?.["lastUpdatedBy"] === user.email ||
				approveCons ||
				(isOMUser(user.roles) && !isNaN(parseInt(defaultValues?.["exu_site"])))
			)
				setIsReadOnly(false);
			else if (
				defaultValues?.["isSent"] === 1 &&
				defaultValues?.["isRejected"] === 0
			)
				setIsReadOnly(true);

			reset({ ...defaultValues });
		}
	}, [siteData, listRetrieved, approveCons, user.email, user.roles, siteCons, clientList, panelList, reset, setValue]);

	useEffect(() => {
		const loadConsInverters = async () => {
			const resolvedTableName = siteData?.table_name || table_name;
			if (!resolvedTableName) {
				return;
			}

			if(is_no_PV) {
				return;
			}

			if(consInvOnceLoaded.current) {
				return;
			}
			consInvOnceLoaded.current = true;

			setIsLoadingInverters(true);
			try {
				const response = await GYAxios.get(
					`/site_creation/cons_inverters/${resolvedTableName}`,
				);
				setOriginalInverters(Array.isArray(response.data) ? response.data : []);
				setEditableInverters(Array.isArray(response.data) ? response.data : []);
			} catch (error) {
				console.log(error);
				Noti.create({
					type: "e",
					msg: "Error loading inverters. Please contact your IT support.",
				});
			} finally {
				setIsLoadingInverters(false);
			}
		};

		loadConsInverters();
	}, [siteData.table_name, table_name, is_no_PV]);

	return (
		<>
			{siteView && (
				<Container fluid id={styles.formSite}>
					{siteAdmin && (
						<Row style={{ margin: "10px 0" }}>
							<Col sm={3} style={{ padding: 0 }}>
								<button
									className="btn btn-success"
									onClick={(e) => setIsReadOnly(!isReadOnly)}
								>
									Edit
								</button>
							</Col>
						</Row>
					)}
					<form onSubmit={handleSubmit(onSubmit, (e) => Noti.create({ type: "e", msg: "Please fill in all required fields." }))}>
						<fieldset disabled={isReadOnly}>
							<Row className={styles.eachLine}>
								<Col sm={6}>
									CLIENT NAME
									<Controller
										control={control}
										name="client"
										defaultValue={null}
										render={({ onChange }) =>
											renderSelect("cr", onChange, clientList, client?.value, null, isExu)
										}
									/>
								</Col>

								<Col sm={6}>
									SOLO/ MULTI
									<Controller
										control={control}
										name="solo"
										defaultValue={null}
										render={({ onChange, value }) =>
											renderSelect("se", onChange, soloMultiList, value)
										}
									/>
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={6}>
									SITE NAME
									{renderInput("site_name", 100, true)}
									{errors.site_name && errors.site_name.type === "required" && (
										<span className={styles.errorDis}>This is required</span>
									)}
								</Col>

								<Col sm={3}>
									SALESFORCE PROJECT ID
									{renderInput("SF_id", 21, true)}
									{errors.SF_id && errors.SF_id.type === "required" && (
										<span className={styles.errorDis}>This is required</span>
									)}
								</Col>
								<Col sm={3}>
									CAPACITY/ POWER (kWp)
									{renderInput(
										"power",
										6,
										false,
										"number",
										fieldShouldDisabled({ type: "is_no_PV", field: "power", check: is_no_PV }),
									)}
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<span className={styles.spanCenter}>
									Display name:{" "}
									{(client?.label || "") + " " + (site_name || "")}
								</span>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									CONTRACT TYPE
									<Controller
										control={control}
										name="contract_id"
										defaultValue={null}
										render={({ onChange, value }) =>
											renderSelect("se", onChange, contractList, value)
										}
									/>
								</Col>
								<Col sm={3}>
									CONTRACT DURATION (YEARS)
									{renderInput("contract_duration", 2, false, "number")}
								</Col>
								<Col sm={3}>
									ESTIMATED COD (DATE)
									<Controller
										name={"cod"}
										control={control}
										defaultValue={null}
										render={({ onChange, value }) =>
											renderDate(value, onChange, isExu)
										}
									/>
								</Col>
								<Col sm={3}>
									ESTIMATED SYNC DATE
									<Controller
										name={"sync"}
										control={control}
										defaultValue={null}
										render={({ onChange, value }) =>
											renderDate(value, onChange, isExu)
										}
									/>
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									NO. OF PYRANOMETER
									{renderInput(
										"nb_irr_sensors",
										2,
										false,
										"number",
										fieldShouldDisabled({
											type: "is_no_PV",
											field: "nb_irr_sensors",
											check: is_no_PV,
										}),
									)}
								</Col>
								<Col sm={3}>
									NO. GRID METER
									{renderInput("nb_grid_meter", 2, false, "number")}
								</Col>
								<Col sm={3}>
									NO. OF INJECTION POINT
									{renderInput(
										"nb_inj_points",
										2,
										false,
										"number",
										fieldShouldDisabled({
											type: "is_no_PV",
											field: "nb_inj_points",
											check: is_no_PV,
										}),
									)}
								</Col>
								<Col sm={3}>
									INVOICING METER TYPE
									<Controller
										control={control}
										name="EDMI_meter"
										defaultValue={invoicingmeter}
										render={({ onChange, value }) =>
											renderSelect(
												"se",
												onChange,
												invoiceMeterList,
												value,
												invoicingmeter,
												fieldShouldDisabled({
													type: "is_no_PV",
													field: "EDMI_meter",
													check: is_no_PV,
												}),
											)
										}
									/>
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									NO. OF INVERTER
									{renderInput(
										"nb_inverter",
										3,
										false,
										"number",
										fieldShouldDisabled({
											type: "is_no_PV",
											field: "nb_inverter",
											check: is_no_PV,
										}),
									)}
								</Col>
								<Col sm={3}>
									INVERTER BRAND + MODEL
									<Controller
										control={control}
										name="inverter_id"
										defaultValue={null}
										render={({ onChange, value }) =>
											renderSelect(
												"se",
												onChange,
												inverterListLegacy,
												value,
												null,
												fieldShouldDisabled({
													type: "is_no_PV",
													field: "inverter_id",
													check: is_no_PV,
												}),
											)
										}
									/>
								</Col>
								<Col sm={3}>
									NO. OF SOLAR PANEL
									{renderInput(
										"nb_solar_modules",
										6,
										false,
										"number",
										fieldShouldDisabled({
											type: "is_no_PV",
											field: "nb_solar_modules",
											check: is_no_PV,
										}),
									)}
								</Col>
								<Col sm={3}>
									SOLAR PANEL BRAND + MODEL
									<Controller
										control={control}
										name="panel"
										defaultValue={null}
										render={({ onChange }) =>
											renderSelect(
												"cr",
												onChange,
												panelList,
												panel?.value,
												null,
												fieldShouldDisabled({
													type: "is_no_PV",
													field: "panel",
													check: is_no_PV,
												}),
											)
										}
									/>
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									NO. OF AMBIENT TEMP. SENSOR
									{renderInput("nb_amb_temp", 5, false, "number")}
								</Col>
								<Col sm={3}>
									NO. OF MODULE TEMP. SENSOR
									{renderInput(
										"nb_mod_temp",
										5,
										false,
										"number",
										fieldShouldDisabled({
											type: "is_no_PV",
											field: "nb_mod_temp",
											check: is_no_PV,
										}),
									)}
								</Col>
								<Col sm={3}>
									REFERENCE VOLTAGE (V)
									{renderInput("reference_voltage", 9, !contract_id?.includes("FIT"), "number", fieldShouldDisabled({ type: "is_FIT_contract", field: "reference_voltage", check: contract_id?.includes("FIT") }))}
									{errors.reference_voltage && errors.reference_voltage.type === "required" && (
										<span className={styles.errorDis}>This is required</span>
									)}
								</Col>
								<Col sm={3}>
									COEFFICIENT KPMAX
									{renderInput("coeff_kpmax", 9, false, "number")}
								</Col> 
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									SITE COUNTRY
									<Controller
										control={control}
										name="country"
										defaultValue={country}
										render={({ onChange, value }) =>
											renderSelect("se", onChange, countries, value, country)
										}
									/>
								</Col>
								<Col sm={3}>
									SITE CITY
									{renderInput("city", 255, false)}
								</Col>
								<Col sm={6}>
									SITE ADDRESS
									{renderInput("address", 255, false)}
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<Col sm={3}>
									SITE ZIPCODE
									{renderInput("zipcode", 7, false, "number")}
								</Col>
								<Col sm={3}>
									SITE GPS
									{renderInput("GPS", 50, false)}
								</Col>
							</Row>

							<Row className={styles.eachLine}>
								<div className={styles.othersDiv}>
									OTHER SENSORS (Leave it blank if no information)
								</div>
								<Col sm={3}>
									NO. OF DGPT2 SENSOR
									{renderInput("DGPT2", 2, false, "number")}
								</Col>
								<Col sm={3}>
									NO. OF WATER SENSOR
									{renderInput("water", 2, false, "number")}
								</Col>
								<Col sm={3}>
									NO. OF WIND DIRECTION SENSOR
									{renderInput("wind_dir", 2, false, "number")}
								</Col>
								<Col sm={3}>
									NO. OF WIND SPEED SENSOR
									{renderInput("wind_speed", 2, false, "number")}
								</Col>
							</Row>

							{(!is_no_PV && siteData.table_name) && (
								<>
									<Row className={styles.eachLine}>
										<div className={styles.othersDiv}>INVERTERS DETAILS</div>
										<Col sm={12}>
											{isLoadingInverters ? (
												<div>Loading inverters...</div>
											) : (
												<div style={{ overflowX: "auto" }}>
													<table className={`table table-bordered ${styles.compareTable}`}>
														<thead>
															<tr>
																<th>INV NO</th>
																<th>SERIAL NUMBER *</th>
																<th>BRAND</th>
																<th>MODEL *</th>
																<th>MODEL NAME</th>
																<th>POWER (kW)</th>
																<th>KWP *</th>
																<th>INSTALLATION DATE *</th>
																<th>COMMENT</th>
																<th>ACTION</th>
															</tr>
														</thead>
														<tbody>
															{editableInverters.map((row, index) => (
																<tr key={`inv-row-${index}`}>
																	<td><input className={styles.inputField} value={row.inv_no ?? ""} disabled /></td>
																	<td><input className={styles.inputField} value={row.serial_number ?? ""} onChange={(e) => onEditableInverterFieldChange(index, "serial_number", e.target.value)} disabled={isReadOnly} required /></td>
																	<td><input className={styles.inputField} value={row.brand ?? ""} readOnly disabled /></td>
																	<td style={{ minWidth: "200px" }}>
																		<Select
																			styles={{
																				container: (baseStyles, state) => ({
																					...baseStyles,
																					width: "calc(100% - 2em)",
																					margin: '0 1em'
																				})
																			}}
																			options={inverterList}
																			value={inverterList.find((opt) => opt.value === row.model) || null}
																			onChange={(selected) => {
																				setEditableInverters((prev) => {
																					const next = prev.map((r, i) =>
																						i === index
																							? {
																								...r,
																								model: selected?.model ?? "",
																								brand: selected?.brand ?? "",
																								model_name: selected?.model_name ?? "",
																								power: selected?.power ?? "",
																							}
																							: r,
																					);
																					syncDerivedInverterFields(next);
																					return next;
																				});
																			}}
																			isDisabled={isReadOnly}
																			isClearable
																			placeholder="Select model..."
																		/>
																	</td>
																	<td><input className={styles.inputField} value={row.model_name ?? ""} readOnly disabled /></td>
																	<td><input className={styles.inputField} value={row.power ?? ""} readOnly disabled /></td>
																	<td><input className={styles.inputField} type="number" value={row.kwp ?? ""} onChange={(e) => onEditableInverterFieldChange(index, "kwp", e.target.value)} disabled={isReadOnly} /></td>
																	<td><input className={styles.inputField} type="date" value={row.installation_date ? moment(row.installation_date).format("YYYY-MM-DD") : ""} onChange={(e) => onEditableInverterFieldChange(index, "installation_date", e.target.value)} disabled={isReadOnly} required /></td>
																	<td><input className={styles.inputField} value={row.comment ?? ""} onChange={(e) => onEditableInverterFieldChange(index, "comment", e.target.value)} disabled={isReadOnly} /></td>
																	<td>
																		<button type="button" className="btn btn-secondary btn-sm" style={{ marginRight: "4px" }} onClick={() => duplicateEditableInverterRow(index)} disabled={isReadOnly}>Duplicate</button>
																		<button type="button" className="btn btn-danger btn-sm" onClick={() => removeEditableInverterRow(index)} disabled={isReadOnly}>Remove</button>
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>
											)}
										</Col>
									</Row>
									<Row className={styles.eachLine}>
										<Col sm={3}>
											<button
												type="button"
												className="btn btn-primary"
												onClick={addEditableInverterRow}
												disabled={isReadOnly}
											>
												Add inverter row
											</button>
										</Col>
									</Row>
								</>
							)}

							<Row className={styles.eachLine}>
								<div className={styles.othersDiv}>BESS CONFIGURATION</div>
								<Col sm={3}>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											marginTop: "10px",
										}}
									>
										<input
											type="checkbox"
											id="is_BESS"
											style={{ marginRight: "8px", width: "auto" }}
											checked={is_BESS}
											{...register("is_BESS")}
											onChange={(e) => {
												setValue("is_BESS", e.target.checked);
												if (!e.target.checked) {
													setValue("BESS_capacity", null);
													setValue("BESS_model", null);
													setValue("BESS_nb_cabinets", 0);
													setValue("is_no_PV", false);
													setValue("BESS_meter_type", null);
												} else {
													setValue("BESS_nb_cabinets", 1);
												}
											}}
											disabled={isReadOnly}
										/>
										<label htmlFor="is_BESS">HAS BESS</label>
									</div>
								</Col>
							</Row>
							<div style={{ display: is_BESS ? "block" : "none" }}>
								<Row className={styles.eachLine}>
									<Col sm={3}>
										BESS CAPACITY (kWh)
										{renderInput("BESS_capacity", 5, is_BESS, "number")}
									</Col>
									<Col sm={3}>
										BESS MODEL
										<Controller
											control={control}
											name="BESS_model"
											defaultValue={null}
											rules={is_BESS ? { required: true } : {}}
											render={({ onChange, value }) =>
												renderSelect("se", onChange, bessModelList, value)
											}
										/>
										{errors.BESS_model &&
											errors.BESS_model.type === "required" && (
												<span className={styles.errorDis}>
													This is required when BESS is selected
												</span>
											)}
									</Col>
									<Col sm={3}>
										NO. OF BESS CABINETS
										{renderInput("BESS_nb_cabinets", 3, is_BESS, "number")}
									</Col>
									<Col sm={3}>
										BESS METER TYPE
										<Controller
											control={control}
											name="BESS_meter_type"
											defaultValue={null}
											rules={is_BESS ? { required: true } : {}}
											render={({ onChange, value }) =>
												renderSelect("se", onChange, bessMeterTypes, value)
											}
										/>
										{errors.BESS_meter_type && errors.BESS_meter_type.type === "required" && (
											<span className={styles.errorDis}>This is required when BESS is selected</span>
										)}
									</Col>
								</Row>
								<Row className={styles.eachLine}>
									<Col sm={3}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												marginTop: "10px",
											}}
										>
											<input
												type="checkbox"
												id="is_no_PV"
												style={{ marginRight: "8px", width: "auto" }}
												checked={is_no_PV}
												{...register("is_no_PV")}
												onChange={(e) => {
													const checked = e.target.checked;
													setValue("is_no_PV", checked);
													if (checked) {
														setDefaultValForNotRequiredField({
															type: "is_no_PV",
														});
													} else {
														syncDerivedInverterFields(editableInverters);
													}
												}}
												disabled={isReadOnly}
											/>
											<label htmlFor="is_no_PV">NO PV</label>
										</div>
									</Col>
								</Row>
							</div>

							{1 === 2 && loggedCountry === "TH" && (
								<Row className={styles.eachLine}>
									<div className={styles.othersDiv}>
										MSA Information (Leave it blank if no information)
									</div>
									<Col sm={3}>
										MSA Price (THB)
										{renderInput("msa_price", 7, false, "number")}
									</Col>
								</Row>
							)}

							<Row className={styles.eachLine}>
								<div className={styles.othersDiv}>
									OTHER COMMENTS
									<Col sm={12}>
										<textarea
											className={styles.textField}
											value={comment !== null ? comment : ""}
											{...register("comment", { maxLength: 500 })}
											onChange={(e) => setValue("comment", e.target.value)}
											maxLength={500}
										/>
									</Col>
								</div>
							</Row>
						</fieldset>

						{consApproval && (
							<Row className={styles.eachLine}>
								<Col sm={6}>
									<input
										type="button"
										value="Approve"
										className={`btn btn-primary ${styles.mainBtns}`}
										onClick={() => approveSite()}
									/>
								</Col>
								<Col sm={6}>
									<input
										type="button"
										value="Reject"
										className={`btn btn-info ${styles.mainBtns}`}
										onClick={() => rejectSite()}
									/>
								</Col>
							</Row>
						)}

						<Row className={styles.eachLine}>
							<Col sm={6}>
								{!isReadOnly && (
									<input
										type="submit"
										value="Save"
										className={`btn btn-primary ${styles.mainBtns}`}
									/>
								)}
							</Col>
							<Col sm={6}>
								<input
									type="button"
									value={consApproval ? "Back" : "Cancel"}
									className={
										consApproval
											? `btn btn-dark ${styles.mainBtns}`
											: `btn btn-primary ${styles.mainBtns}`
									}
									onClick={() => history.push(constructionTablePath)}
								/>
							</Col>
						</Row>
					</form>
				</Container>
			)}

			{needLoader && (
				<div className="overlay translucent" style={showLoader}>
					<Loader
						translucent={true}
						customStyle={{
							width: "150px",
							height: "150px",
							position: "fixed",
							left: "50%",
							top: "35%",
						}}
					/>
				</div>
			)}
		</>
	);
};
